import { Message } from 'talkjs/all';
import { QueryKey } from '@tanstack/react-query';
import { ConversationsPage, UserConversation } from '../../types/chats';
import client from '../../utils/react-query/client';
import { isCuid } from '../global';
import { LoadedUser } from '../../interfaces';
import { getInitials } from '../../utils/global/helper';

export function getParticipantId(id: string) {
  return id.split('-')[0];
}

export function removeConversationFromList(
  queryKey: QueryKey,
  conversationId: string,
) {
  client.setQueryData(
    queryKey,
    (oldData: { pages: ConversationsPage[]; pageParams: number[] }) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          result: page.result.filter(c => c.id !== conversationId),
        })),
      };
    },
  );
}

export function updateConversationInList(
  queryKey: QueryKey,
  updatedConversation: UserConversation,
  placeInTop = false,
) {
  client.setQueryData(
    queryKey,
    (oldData: { pages: ConversationsPage[]; pageParams: number[] }) => {
      if (!oldData) return oldData;
      // Remove from current position and add to the top
      const newPages = oldData.pages.map(page => ({
        ...page,
        result: placeInTop
          ? page.result.filter(c => c.id !== updatedConversation.id)
          : page.result.map(c =>
              c.id === updatedConversation.id ? updatedConversation : c,
            ),
      }));
      if (placeInTop) newPages[0].result.unshift(updatedConversation);
      return {
        ...oldData,
        pages: newPages,
      };
    },
  );
}

export function updateExistingConversation(
  conversation: UserConversation,
  messageData: Message,
): UserConversation {
  return {
    ...conversation,
    lastMessageAt: messageData.timestamp,
    unreadMessageCount: (conversation.unreadMessageCount ?? 0) + 1,
    lastMessage: {
      conversationId: messageData.conversation.id,
      text: messageData?.body,
      createdAt: messageData.timestamp,
      editedAt: conversation?.lastMessage?.editedAt || null,
      readBy: conversation.lastMessage?.readBy || [],
      referencedMessageId: null,
      attachment: messageData?.attachment?.dimensions
        ? {
            dimensions: messageData.attachment.dimensions,
            size: messageData.attachment.size,
            url: messageData.attachment.url,
          }
        : null,
      custom: messageData.custom,
      id: messageData.id,
      location: messageData.location,
      origin: messageData.origin,
      senderId: messageData.sender?.id,
      type: messageData.type,
    },
  } as UserConversation;
}

export function getAppointmentIdFromConversation(
  conversation: UserConversation,
) {
  return (
    conversation.custom?.appointment_id ||
    conversation.custom?.request_id ||
    (isCuid(conversation.id) ? conversation.id : null)
  );
}

export function getChatParticipants(
  conversation: UserConversation,
  user: LoadedUser,
) {
  const customerParticipant = conversation.participants.find(
    p => p.role === 'CUSTOMER' || p.participantId.endsWith('-customer'),
  );
  const participant =
    customerParticipant ??
    conversation.participants.find(p => p.userId !== user.userId) ??
    conversation.participants[0];
  const initials = participant ? getInitials(participant) : '';

  const otherParticipants = conversation.participants.filter(
    p => p.userId !== participant.userId && p.userId !== user.userId,
  );

  return {
    customerParticipant: participant,
    otherParticipants,
    initials,
  };
}

export function getParticipantDisplayName(
  participant: UserConversation['participants'][number],
  fullName = false,
) {
  const lastNameInitial = participant?.lastName ? participant.lastName[0] : '';
  const lastName = (
    fullName ? participant?.lastName || '' : `${lastNameInitial}.`
  ).trim();
  const firstName = (participant?.firstName || '').trim();
  return `${firstName}${lastName ? ` ${lastName}` : ''}`;
}

export function getArchivedKey(userId: string) {
  return `${userId}:archived`;
}

export function isConversationArchived({
  conversation,
  userId,
}: {
  conversation: Pick<UserConversation, 'custom'>;
  userId: string;
}) {
  const archivedKey = getArchivedKey(userId);
  return conversation?.custom?.[archivedKey] === 'true';
}

export function getImportantKey(userId: string) {
  return `${userId}:important`;
}

export function isConversationImportant({
  conversation,
  userId,
}: {
  conversation: Pick<UserConversation, 'custom'>;
  userId: string;
}) {
  const importantKey = getImportantKey(userId);
  return conversation?.custom?.[importantKey] === 'true';
}
